import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const PriceMonitor = ({ tokenMint, onPriceUpdate, showTrend = true, showHistory = false }) => {
  const { tokens } = useAuth();
  const { showError, showSuccess, showWarning } = useToast();
  const [priceData, setPriceData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepumpbackend.urion.live/api';
  const WS_BASE_URL = process.env.REACT_APP_WS_URL || 'wss://safepumpbackend.urion.live/ws';

  const connectWebSocket = useCallback(() => {
    if (!tokens?.access_token || !tokenMint) {
      return;
    }

    try {
      const ws = new WebSocket(WS_BASE_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('🔌 WebSocket connected');
        setIsConnected(true);
        setError(null);
        reconnectAttempts.current = 0;
        
        // Show success toast only on reconnection (not initial connection)
        if (reconnectAttempts.current > 0) {
          showSuccess('Price feed reconnected');
        }

        // Authenticate
        ws.send(JSON.stringify({
          type: 'authenticate',
          token: tokens.access_token
        }));
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleWebSocketMessage(message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = () => {
        console.log('🔌 WebSocket disconnected');
        setIsConnected(false);
        
        // Show warning for disconnection
        if (reconnectAttempts.current === 0) {
          showWarning('Price feed disconnected, attempting to reconnect...');
        }
        
        // Attempt to reconnect
        if (reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log(`🔄 Reconnecting... (attempt ${reconnectAttempts.current})`);
            connectWebSocket();
          }, delay);
        } else {
          const errorMsg = 'Price feed connection failed after multiple attempts';
          setError(errorMsg);
          showError(errorMsg);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        const errorMsg = 'Price feed connection error';
        setError(errorMsg);
        showError(errorMsg);
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      setError('Failed to establish WebSocket connection');
    }
  }, [tokens?.access_token, tokenMint, WS_BASE_URL]);

  const handleWebSocketMessage = (message) => {
    switch (message.type) {
      case 'authenticated':
        console.log('✅ WebSocket authenticated');
        // Subscribe to price updates
        wsRef.current?.send(JSON.stringify({
          type: 'subscribe_price',
          tokenMint
        }));
        break;

      case 'subscription_confirmed':
        console.log(`📊 Subscribed to price updates for ${tokenMint}`);
        setLoading(false);
        break;

      case 'price_update':
        if (message.tokenMint === tokenMint) {
          setPriceData(message.data);
          if (onPriceUpdate) {
            onPriceUpdate(message.data);
          }
        }
        break;

      case 'trend_response':
        if (message.tokenMint === tokenMint) {
          setTrendData(message.data);
        }
        break;

      case 'error':
        console.error('WebSocket error:', message.message);
        setError(message.message);
        break;

      case 'auth_error':
        console.error('WebSocket auth error:', message.message);
        setError('Authentication failed');
        break;

      default:
        console.log('Unknown WebSocket message:', message);
    }
  };

  const fetchTrendAnalysis = useCallback(async () => {
    if (!showTrend || !tokens?.access_token || !tokenMint) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/prices/${tokenMint}/trend`, {
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTrendData(data.data.trend);
      }
    } catch (error) {
      console.error('Error fetching trend analysis:', error);
    }
  }, [showTrend, tokens?.access_token, tokenMint, API_BASE_URL]);

  const fetchPriceHistory = useCallback(async () => {
    if (!showHistory || !tokens?.access_token || !tokenMint) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/prices/${tokenMint}/history?timeframe=4h&limit=20`, {
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPriceHistory(data.data.history);
      }
    } catch (error) {
      console.error('Error fetching price history:', error);
    }
  }, [showHistory, tokens?.access_token, tokenMint, API_BASE_URL]);

  const requestTrendAnalysis = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'get_trend',
        tokenMint
      }));
    }
  };

  useEffect(() => {
    if (tokenMint && tokens?.access_token) {
      connectWebSocket();
      
      if (showTrend) {
        fetchTrendAnalysis();
      }
      
      if (showHistory) {
        fetchPriceHistory();
      }
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [tokenMint, tokens?.access_token, connectWebSocket, fetchTrendAnalysis, fetchPriceHistory]);

  const formatPrice = (price) => {
    if (!price || price === 0) return '$0.00';
    if (price < 0.000001) return `$${price.toExponential(2)}`;
    if (price < 0.01) return `$${price.toFixed(6)}`;
    if (price < 1) return `$${price.toFixed(4)}`;
    return `$${price.toFixed(2)}`;
  };

  const formatPercentage = (percent) => {
    if (percent === 0) return '0.00%';
    const formatted = percent.toFixed(2);
    return `${percent >= 0 ? '+' : ''}${formatted}%`;
  };

  const getTrendColor = (direction) => {
    switch (direction) {
      case 'upward': return '#28a745';
      case 'downward': return '#dc3545';
      case 'sideways': return '#ffc107';
      default: return '#6c757d';
    }
  };

  const getTrendIcon = (direction) => {
    switch (direction) {
      case 'upward': return '📈';
      case 'downward': return '📉';
      case 'sideways': return '➡️';
      default: return '❓';
    }
  };

  if (loading) {
    return (
      <div className="price-monitor loading">
        <div className="loading-spinner"></div>
        <p>Connecting to price feed...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="price-monitor error">
        <div className="error-content">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={connectWebSocket} className="retry-button">
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="price-monitor">
      <div className="connection-status">
        <div className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
          {isConnected ? '🟢' : '🔴'}
        </div>
        <span className="status-text">
          {isConnected ? 'Live' : 'Disconnected'}
        </span>
      </div>

      {priceData && (
        <div className="price-display">
          <div className="current-price">
            <span className="price-label">Current Price</span>
            <span className="price-value">{formatPrice(priceData.price)}</span>
          </div>
          
          <div className="price-change">
            <span className="change-label">24h Change</span>
            <span className={`change-value ${priceData.priceChange24h >= 0 ? 'positive' : 'negative'}`}>
              {formatPercentage(priceData.priceChange24h)}
            </span>
          </div>
          
          <div className="last-updated">
            <span>Updated: {new Date(priceData.timestamp).toLocaleTimeString()}</span>
          </div>
        </div>
      )}

      {showTrend && trendData && (
        <div className="trend-analysis">
          <div className="trend-header">
            <h4 style={{color:'lightgreen'}}>Trend Analysis</h4>
            <button onClick={requestTrendAnalysis} className="refresh-trend">
              🔄
            </button>
          </div>
          
          <div className="trend-content">
            <div className="trend-direction">
              <span className="trend-icon">{getTrendIcon(trendData.direction)}</span>
              <span 
                className="trend-text"
                style={{ color: getTrendColor(trendData.direction) }}
              >
                {trendData.direction.toUpperCase()}
              </span>
            </div>
            
            <div className="trend-metrics">
              <div className="metric">
                <span className="metric-label">Strength</span>
                <span className="metric-value">{trendData.strength}%</span>
              </div>
              <div className="metric">
                <span className="metric-label">Confidence</span>
                <span className="metric-value">{trendData.confidence}%</span>
              </div>
            </div>
            
            <div className="trend-analysis-text">
              <p style={{color: 'lightcyan'}}>{trendData.analysis}</p>
            </div>
          </div>
        </div>
      )}

      {showHistory && priceHistory.length > 0 && (
        <div className="price-history">
          <h4>Recent Price History</h4>
          <div className="history-chart">
            {priceHistory.slice(-10).map((point, index) => (
              <div key={index} className="history-point">
                <div className="point-time">
                  {new Date(point.timestamp).toLocaleTimeString()}
                </div>
                <div className="point-price">
                  {formatPrice(point.price)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceMonitor;